import { useState } from "react";
import { Table, Select, TextInput, Textarea } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import styles from "./EditableTable.module.css";

const EditableTable = ({
  columns,
  data,
  onChange,
  role = [],
  compact = false,
  onRowClick,
  doubleClickRow = false,
}) => {
  const [fileNames, setFileNames] = useState({});

  const renderCell = (row, col) => {
    const value = row[col.key];
    const editable =
      typeof col.editable === "function" ? col.editable(role) : col.editable;
    if (editable) {
      const handleChange = (val) => {
        onChange(row.id, col.key, val);
      };

      switch (col.type) {
        case "select":
          return (
            <Select
              data={col.options}
              value={String(value) || ""}
              onChange={handleChange}
              searchable
              className={`${styles[col.key]} ${styles[String(value)?.toLowerCase()]}`}
            />
          );
        case "date":
          return (
            <DateInput
              value={value ? new Date(value) : null}
              onChange={(date) =>
                handleChange(date?.toISOString().split("T")[0])
              }
              valueFormat="YYYY-MM-DD"
            />
          );
        case "file":
          return (
            <div>
              {value && (
                <div style={{ marginBottom: "4px" }}>
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    style={{ fontSize: "12px", display: "inline-block" }}
                  >
                    View Current File
                  </a>
                </div>
              )}
              <input
                type="file"
                accept={col.accept}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const formData = new FormData();
                    formData.append(col.key, file);
                    onChange(row.id, col.key, formData, true);
                    setFileNames((prev) => ({
                      ...prev,
                      [`${row.id}-${col.key}`]: file.name,
                    }));
                  }
                }}
              />
              {fileNames[`${row.id}-${col.key}`] && (
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  <strong>Uploaded:</strong> {fileNames[`${row.id}-${col.key}`]}
                </div>
              )}
            </div>
          );
        case "textarea":
          return (
            <Textarea
              value={value || ""}
              autosize
              minRows={1}
              maxRows={6}
              onChange={(e) => handleChange(e.target.value)}
            />
          );

        default:
          return (
            <TextInput
              value={value || ""}
              onChange={(e) => handleChange(e.target.value)}
            />
          );
      }
    }

    if (col.render) return col.render(value, row);
    return <span className={compact ? styles.compactText : undefined}>{value ?? "N/A"}</span>;
  };

  const isHighlighted = (row) =>
    !row.issuance_representative &&
    row.created_on &&
    new Date(row.created_on).toDateString() === new Date().toDateString();

  return (
    <div className={compact ? styles.compactWrapper : styles.wrapper}>
      <Table
        striped={compact}
        highlightOnHover
        withTableBorder={!compact}
        withColumnBorders
        className={compact ? styles.compactTable : undefined}
      >
        <Table.Thead className={compact ? styles.compactThead : undefined}>
          <Table.Tr>
            {columns.map((col, index) => (
              <Table.Th
                key={col.key}
                className={compact ? styles.compactTh : undefined}
                style={{
                  position: compact && index === 0 ? "sticky" : undefined,
                  left: compact && index === 0 ? 0 : undefined,
                  zIndex: compact && index === 0 ? 3 : undefined,
                  minWidth: col.width || (compact ? 120 : 150),
                }}
              >
                {col.label}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data?.map((row) => {
            const highlighted = isHighlighted(row);
            return (
              <Table.Tr
                key={row.id}
                className={[
                  compact ? styles.compactRow : undefined,
                  highlighted ? styles.highlightRow : undefined,
                  onRowClick ? styles.clickableRow : undefined,
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={onRowClick && !doubleClickRow ? () => onRowClick(row) : undefined}
                onDoubleClick={onRowClick && doubleClickRow ? () => onRowClick(row) : undefined}
              >
                {columns.map((col, index) => (
                  <Table.Td
                    key={col.key}
                    className={compact ? styles.compactTd : undefined}
                    style={{
                      position: compact && index === 0 ? "sticky" : undefined,
                      left: compact && index === 0 ? 0 : undefined,
                      zIndex: compact && index === 0 ? 2 : undefined,
                      minWidth: col.width || (compact ? 120 : 150),
                      backgroundColor:
                        compact && index === 0
                          ? highlighted
                            ? "#fff4e6"
                            : "var(--mantine-color-body)"
                          : undefined,
                    }}
                  >
                    {renderCell(row, col)}
                  </Table.Td>
                ))}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </div>
  );
};

export default EditableTable;